/**
 * Whether a keystroke asked to undo or redo.
 *
 * Pure, and given only what it needs, so the awkward parts are testable without
 * a keyboard: the platform split between Cmd and Ctrl, the two spellings of redo,
 * and the rule that matters most -- a keystroke where the user is typing belongs
 * to that field. `Ctrl+Z` in a text box has to undo the typing, not the last
 * thing done to the database, and a global handler that swallows it makes every
 * input in the application feel broken.
 */
export interface KeyStroke {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  shiftKey: boolean
  /** Whether the keystroke landed in an input, a textarea or a rich text field. */
  inEditableField: boolean
}

export function undoRedoIntent(stroke: KeyStroke): 'undo' | 'redo' | null {
  if (stroke.inEditableField) return null
  if (!stroke.ctrlKey && !stroke.metaKey) return null

  const key = stroke.key.toLowerCase()

  // `Ctrl+Y` is redo on Windows and unclaimed on a Mac, so accepting it
  // everywhere costs nothing and saves knowing which one you are on.
  if (key === 'y') return 'redo'
  if (key !== 'z') return null

  return stroke.shiftKey ? 'redo' : 'undo'
}

/** Whether an event landed somewhere the user is typing. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true

  const tag = target.tagName
  if (tag === 'TEXTAREA') return true
  if (tag !== 'INPUT') return false

  // Buttons, checkboxes and radios are inputs with nothing to type into, so a
  // keystroke on one of them is not the field's to keep.
  const type = (target as HTMLInputElement).type
  return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range'].includes(type)
}
